/* Xocolata — animation system (GSAP + ScrollTrigger, vendored locally) */
(function () {
  'use strict';

  // Keep the sticky ticker positioned right below Shopify's own sticky header,
  // whatever height that header happens to be (varies by viewport/announcement bar).
  function syncHeaderHeight() {
    var header = document.querySelector('header-component') || document.querySelector('.shopify-section-group-header-group');
    var h = header ? header.getBoundingClientRect().height : 60;
    document.documentElement.style.setProperty('--xh-header-h', h + 'px');
  }
  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);
  window.addEventListener('load', syncHeaderHeight);

  // Safety net: if GSAP fails to load/init for any reason, or a reveal never
  // fires, nothing on this commerce page should stay permanently invisible.
  window.setTimeout(function () {
    document.querySelectorAll('[data-anim]').forEach(function (el) {
      if (getComputedStyle(el).opacity === '0') {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }
    });
  }, 2500);

  // ---- Autoplay fallback: some mobile browsers (Android Chrome Data Saver/Lite
  // mode, low-power mode, certain OEM browsers) silently refuse to autoplay video
  // even with autoplay+muted+playsinline all correctly set — instead of playing,
  // they render their own poster + play-button UI, which looks broken since we
  // never intended a manual player. Every one of these <video> elements sits on
  // top of a container that already has the same photo as a CSS background-image
  // (used for prefers-reduced-motion), so if autoplay doesn't actually start, we
  // just hide the <video> and let that background photo show through cleanly. ----
  document.querySelectorAll('video[autoplay]').forEach(function (video) {
    var hideIfNotPlaying = function () {
      if (video.paused) video.style.display = 'none';
    };
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(hideIfNotPlaying);
    }
    // Belt-and-suspenders: some browsers block autoplay without ever rejecting
    // the play() promise (e.g. Data Saver mode) — catch those too.
    window.setTimeout(hideIfNotPlaying, 2000);
  });

  // ---- Floating CTA: appears once the visitor scrolls past the hero ----
  // (No GSAP dependency — must keep working even if GSAP fails to load.)
  var floatCta = document.querySelector('[data-xh-float-cta]');
  var hero = document.querySelector('.xh-hero');
  if (floatCta && hero && 'IntersectionObserver' in window) {
    var heroIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        floatCta.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { threshold: 0.05 });
    heroIo.observe(hero);
  } else if (floatCta) {
    floatCta.classList.add('is-visible');
  }

  // ---- Collection carousel ----
  // (No GSAP dependency — must keep working even if GSAP fails to load. A previous
  // version of this file gated everything below a "GSAP loaded?" check, which meant
  // if GSAP ever failed to load — ad blocker, blocked script, network hiccup — the
  // carousel's click handlers never got attached at all and nothing moved. Decorative
  // reveal animations depending on GSAP are the only thing allowed to skip below.)
  document.querySelectorAll('[data-xh-carousel]').forEach(function (carousel) {
    var section = carousel.closest('.xh-coleccion');
    var wrap = carousel.closest('.xh-carousel-wrap');
    var progress = section ? section.querySelector('[data-xh-progress]') : null;
    var cards = Array.prototype.slice.call(carousel.querySelectorAll('[data-ccard]'));
    var segs = progress ? Array.prototype.slice.call(progress.querySelectorAll('[data-seg]')) : [];
    if (!cards.length) return;

    // A regular mouse only has a vertical wheel — with no visible scrollbar and no
    // trackpad, there was previously no way to move this carousel at all. Redirect
    // vertical wheel input into horizontal scroll, but ONLY for events that look like
    // a plain mouse wheel (deltaX exactly 0). Trackpads report their own real deltaX
    // for horizontal swipes, which the browser already scrolls natively — redirecting
    // deltaY on top of that fought the native gesture and produced erratic jumps.
    carousel.addEventListener('wheel', function (e) {
      if (e.deltaX !== 0 || e.deltaY === 0) return;
      var atStart = carousel.scrollLeft <= 0;
      var atEnd = carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      carousel.scrollLeft += e.deltaY;
      e.preventDefault();
    }, { passive: false });

    // Mouse drag-to-scroll (trackpad/touch already scroll natively).
    var isDown = false, startX = 0, startScroll = 0, moved = false;
    carousel.addEventListener('mousedown', function (e) {
      isDown = true; moved = false;
      startX = e.pageX; startScroll = carousel.scrollLeft;
      carousel.classList.add('is-dragging');
    });
    window.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 4) moved = true;
      carousel.scrollLeft = startScroll - dx;
    });
    window.addEventListener('mouseup', function () {
      if (!isDown) return;
      isDown = false;
      carousel.classList.remove('is-dragging');
    });
    carousel.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // No stored "current index" anywhere, and no per-card "ideal pixel position"
    // either (both were tried and both broke down). Card width capped at 380px means
    // on wide screens the carousel can run out of physical scroll room before every
    // card gets its own fully-reachable position — Almojábana and Pandebono could
    // both have "ideal" positions beyond the true max scroll, so distance-matching
    // against those ideals meant one card could never win, causing the active
    // highlight to jump straight past it (Buñuelo → Pandebono, skipping Almojábana).
    //
    // Instead: divide however much scrollable range *actually exists* (maxScroll)
    // into N-1 equal steps (N = card count) and use the scroll ratio to pick an
    // index. Every card gets a fair, guaranteed-reachable slice of the real range,
    // no matter how compressed that range is relative to the cards' own widths.
    function maxScroll() {
      return Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    }

    function nearestIndex() {
      var max = maxScroll();
      if (max <= 0) return 0;
      var ratio = carousel.scrollLeft / max;
      return Math.max(0, Math.min(cards.length - 1, Math.round(ratio * (cards.length - 1))));
    }

    function render() {
      var idx = nearestIndex();
      cards.forEach(function (card, i) {
        card.classList.toggle('is-active', i === idx);
      });
      segs.forEach(function (seg, i) {
        var fill = seg.querySelector('.xh-progress__fill');
        if (fill) fill.style.width = (i <= idx ? 100 : 0) + '%';
        seg.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
    }

    var renderTimer;
    carousel.addEventListener('scroll', function () {
      clearTimeout(renderTimer);
      renderTimer = setTimeout(render, 60);
    }, { passive: true });

    // Targets the same proportional slice of the real scrollable range that
    // nearestIndex()/render() use, so "go to card i" and "which card is active"
    // always agree — no separate per-card pixel math to fall out of sync.
    function goTo(i) {
      i = Math.max(0, Math.min(cards.length - 1, i));
      var max = maxScroll();
      var target = cards.length > 1 ? (i / (cards.length - 1)) * max : 0;
      var delta = target - carousel.scrollLeft;
      if (Math.abs(delta) < 1) return;
      carousel.scrollBy({ left: delta, behavior: 'smooth' });
    }

    segs.forEach(function (seg, i) {
      seg.addEventListener('click', function () { goTo(i); });
    });

    var prevBtn = wrap ? wrap.querySelector('[data-xh-arrow="prev"]') : null;
    var nextBtn = wrap ? wrap.querySelector('[data-xh-arrow="next"]') : null;
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(nearestIndex() - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(nearestIndex() + 1); });

    render();
  });

  // ---- Decorative entrance reveals — the ONLY thing allowed to depend on GSAP ----
  if (typeof gsap === 'undefined') return;

  // Reveals: GSAP tweens triggered by IntersectionObserver, NOT ScrollTrigger's
  // pre-calculated pixel positions. ScrollTrigger computes "when to reveal" from a
  // snapshot of layout taken at init time; at some window sizes that snapshot goes
  // stale (fonts/layout settle differently) and the trigger point never gets hit —
  // content stays at opacity:0 forever until something forces a recalculation
  // (e.g. maximizing the window). Confirmed real bug. IntersectionObserver has no
  // such snapshot — it's evaluated by the browser against live layout continuously,
  // so it can't desync the same way.
  var motionOK = !window.matchMedia || window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

  function revealGroup(el, items, duration, stagger) {
    if (!motionOK || !('IntersectionObserver' in window)) return; // stays fully visible, no animation
    gsap.set(items, { opacity: 0, y: items === el ? 26 : 30 });
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        gsap.to(items, { opacity: 1, y: 0, duration: duration, ease: 'power3.out', stagger: stagger || 0 });
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
  }

  document.querySelectorAll('[data-anim="fade-up"]').forEach(function (el) {
    revealGroup(el, el, 0.8, 0);
  });

  document.querySelectorAll('[data-anim="stagger"]').forEach(function (group) {
    var items = Array.prototype.slice.call(group.children);
    revealGroup(group, items, 0.7, 0.12);
  });
})();
